import React, { useEffect, useState } from 'react';
import { FaTimes, FaCheck, FaSearch, FaUser, FaTags, FaVideo, FaImage, FaUpload, FaTrash } from 'react-icons/fa';
import { getComediantes } from '../../../../../components/http/Actions/ComediantesAction';
import { getTags } from '../../../../../components/http/Actions/TagsAction';

const CreateVideoShowModal = ({ newVideoShow, setNewVideoShow, handleCreateSubmit, isLoading, onClose }) => {
  const [comediantes, setComediantes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingResources, setLoadingResources] = useState(true);
  const [error, setError] = useState('');
  const [searchComediante, setSearchComediante] = useState('');
  const [searchTag, setSearchTag] = useState('');
  
  // Estados para archivos
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState(null);
  
  // Estados para validación
  const [videoError, setVideoError] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // Estados para la paginación
  const [comediantesPage, setComediantesPage] = useState(1);
  const [showSelectedComediantesOnly, setShowSelectedComediantesOnly] = useState(false);
  const itemsPerPage = 10;

  // Cargar comediantes y tags
  useEffect(() => {
    const fetchResources = async () => {
      try {
        await getComediantes(
          (data) => setComediantes(data.data || []),
          (err) => setError('Error al cargar comediantes: ' + err)
        );
        
        await getTags(
          (data) => setTags(data.data || []),
          (err) => setError('Error al cargar tags: ' + err)
        );
      } catch (err) {
        setError('Error al cargar recursos');
      } finally {
        setLoadingResources(false);
      }
    };
    
    fetchResources();
  }, []);

  // Función de validación
  const validateVideoInput = (showError = false) => {
    const hasVideoFile = selectedVideoFile !== null || newVideoShow.videoFile !== null;
    const hasVideoURL = newVideoShow.videoURL && newVideoShow.videoURL.trim() !== '';
    
    if (!hasVideoFile && !hasVideoURL) {
      if (showError) {
        setVideoError('Debe proporcionar un archivo de video o una URL de video');
      }
      return false;
    }
    
    if (hasVideoFile && hasVideoURL) {
      if (showError) {
        setVideoError('Proporcione solo un archivo de video O una URL de video, no ambos');
      }
      return false;
    }
    
    setVideoError('');
    return true;
  };

  // Función para limpiar archivo de video
  const clearVideoFile = () => {
    setSelectedVideoFile(null);
    setNewVideoShow(prev => ({
      ...prev,
      videoFile: null
    }));
    
    if (hasAttemptedSubmit) {
      setTimeout(() => validateVideoInput(true), 100);
    }
  };

  // Función para limpiar URL de video
  const clearVideoURL = () => {
    setNewVideoShow(prev => ({
      ...prev,
      videoURL: ''
    }));
    
    if (hasAttemptedSubmit) {
      setTimeout(() => validateVideoInput(true), 100);
    }
  };

  // Función para limpiar archivo de miniatura
  const clearThumbnailFile = () => {
    setSelectedThumbnailFile(null);
    setNewVideoShow(prev => ({
      ...prev,
      thumbnailFile: null
    }));
  };

  // Función para limpiar URL de imagen
  const clearImageURL = () => {
    setNewVideoShow(prev => ({
      ...prev,
      imagen: ''
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "videoURL" && value.trim() !== '') {
      // Limpiar archivo de video si se ingresa URL
      setSelectedVideoFile(null);
      setNewVideoShow(prev => ({
        ...prev,
        videoFile: null,
        [name]: value
      }));
    } else if (name === "imagen" && value.trim() !== '') {
      // Limpiar archivo de miniatura si se ingresa URL de imagen
      setSelectedThumbnailFile(null);
      setNewVideoShow(prev => ({
        ...prev,
        thumbnailFile: null,
        [name]: value
      }));
    } else if (name === "private") {
      // Manejar el toggle de privacidad
      setNewVideoShow(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === "duracion") {
      setNewVideoShow(prev => ({
        ...prev,
        [name]: parseInt(value) || 0
      }));
    } else {
      setNewVideoShow(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (hasAttemptedSubmit && (name === "videoURL" || name === "imagen")) {
      setTimeout(() => validateVideoInput(true), 100);
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedVideoFile(file);
      setNewVideoShow(prev => ({
        ...prev,
        videoFile: file,
        videoURL: '' // Limpiar URL cuando se selecciona archivo
      }));
    }
    
    if (hasAttemptedSubmit) {
      setTimeout(() => validateVideoInput(true), 100);
    }
  };

  const handleThumbnailFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedThumbnailFile(file);
      setNewVideoShow(prev => ({
        ...prev,
        thumbnailFile: file,
        imagen: '' // Limpiar URL de imagen cuando se selecciona archivo
      }));
    }
  };

  const handleComedianteChange = (comedianteId) => {
    if (newVideoShow.comediantesIds.includes(comedianteId)) {
      setNewVideoShow(prev => ({
        ...prev,
        comediantesIds: prev.comediantesIds.filter(id => id !== comedianteId)
      }));
    } else {
      setNewVideoShow(prev => ({
        ...prev,
        comediantesIds: [...prev.comediantesIds, comedianteId]
      }));
    }
  };

  const handleTagChange = (tagId) => {
    if (newVideoShow.tagsIds.includes(tagId)) {
      setNewVideoShow(prev => ({
        ...prev,
        tagsIds: prev.tagsIds.filter(id => id !== tagId)
      }));
    } else {
      setNewVideoShow(prev => ({
        ...prev,
        tagsIds: [...prev.tagsIds, tagId]
      }));
    }
  };

  const isColorDark = (hexColor) => {
    if (!hexColor || !hexColor.startsWith('#')) return false;
    
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance < 0.5;
  };

  const filteredComediantes = comediantes.filter(comediante => {
    const matchesSearch = comediante.nombre.toLowerCase().includes(searchComediante.toLowerCase());
    const isSelected = newVideoShow.comediantesIds.includes(comediante.comedianteId);
    
    if (showSelectedComediantesOnly) {
      return isSelected && matchesSearch;
    }
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredComediantes.length / itemsPerPage);
  const paginatedComediantes = filteredComediantes.slice(
    (comediantesPage - 1) * itemsPerPage,
    comediantesPage * itemsPerPage
  );

  const filteredTags = tags.filter(tag => 
    tag.nombre.toLowerCase().includes(searchTag.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
      <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-5xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-white">Nuevo Video</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {hasAttemptedSubmit && videoError && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
            <p className="text-sm">{videoError}</p>
          </div>
        )}
        
        {loadingResources ? (
          <div className="flex justify-center my-8">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <form onSubmit={(e) => {
            e.preventDefault();
            setHasAttemptedSubmit(true);
            
            if (validateVideoInput(true)) {
              handleCreateSubmit(e, newVideoShow);
            }
          }} className="space-y-8">
            
          {/* INFORMACIÓN BÁSICA */}
          <div className="space-y-6">
            {/* Primera fila: Título y Fecha */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Título
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={newVideoShow.titulo}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Fecha de Estreno
                </label>
                <input
                  type="date"
                  name="fechaEstreno"
                  value={newVideoShow.fechaEstreno}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
            </div>

            {/* Descripción - Campo completo */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Descripción
              </label>
              <textarea
                name="descripcion"
                value={newVideoShow.descripcion}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 resize-none"
                required
                placeholder="Escribe una descripción del video..."
              ></textarea>
            </div>

            {/* Segunda fila: Duración, Privacidad y Premium */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Duración (minutos)
                </label>
                <input
                  type="number"
                  name="duracion"
                  value={newVideoShow.duracion}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Estado de Privacidad
                </label>
                <div className="flex items-center space-x-3 pt-2">
                  <span className={`text-sm font-medium ${!newVideoShow.private ? 'text-green-400' : 'text-gray-400'}`}>
                    Público
                  </span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="private"
                      checked={newVideoShow.private}
                      onChange={(e) => setNewVideoShow(prev => ({
                        ...prev,
                        private: e.target.checked
                      }))}
                      className="sr-only"
                      id="privacy-toggle-create"
                    />
                    <label
                      htmlFor="privacy-toggle-create"
                      className={`flex items-center cursor-pointer w-12 h-6 rounded-full p-1 transition-all duration-300 ${
                        newVideoShow.private ? 'bg-red-600' : 'bg-green-600'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                          newVideoShow.private ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </label>
                  </div>
                  <span className={`text-sm font-medium ${newVideoShow.private ? 'text-red-400' : 'text-gray-400'}`}>
                    Privado
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {newVideoShow.private ? 'Solo el comediante y administrador pueden ver este video' : 'Todos pueden ver este video'}
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Tipo de Acceso
                </label>
                <div className="flex items-center space-x-3 pt-2">
                  <span className={`text-sm font-medium ${!newVideoShow.isPremium ? 'text-blue-400' : 'text-gray-400'}`}>
                    Gratuito
                  </span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="isPremium"
                      checked={newVideoShow.isPremium}
                      onChange={(e) => setNewVideoShow(prev => ({
                        ...prev,
                        isPremium: e.target.checked
                      }))}
                      className="sr-only"
                      id="premium-toggle-create"
                    />
                    <label
                      htmlFor="premium-toggle-create"
                      className={`flex items-center cursor-pointer w-12 h-6 rounded-full p-1 transition-all duration-300 ${
                        newVideoShow.isPremium ? 'bg-yellow-600' : 'bg-blue-600'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                          newVideoShow.isPremium ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </label>
                  </div>
                  <span className={`text-sm font-medium ${newVideoShow.isPremium ? 'text-yellow-400' : 'text-gray-400'}`}>
                    Premium
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {newVideoShow.isPremium ? 'Solo usuarios premium pueden ver este video' : 'Todos los usuarios pueden ver este video'}
                </p>
              </div>
            </div>
          </div>
            {/* SECCIÓN DE VIDEO */}
            <div className="border-t border-zinc-700 pt-8">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
                <FaVideo className="mr-2 text-red-500" />
                Video (Obligatorio)
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Subir Archivo de Video */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-white flex items-center">
                      <FaUpload className="mr-2 text-red-500" />
                      Subir Archivo de Video
                    </label>
                    {/* Botón para limpiar archivo */}
                    {(selectedVideoFile || newVideoShow.videoFile) && (
                      <button
                        type="button"
                        onClick={clearVideoFile}
                        className="text-red-400 hover:text-red-300 p-1 rounded transition-colors"
                        title="Eliminar archivo seleccionado"
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
                    (selectedVideoFile || newVideoShow.videoFile) ? 'border-green-500 bg-green-500/10' : 
                    newVideoShow.videoURL ? 'border-gray-600 bg-gray-500/10' : 
                    'border-zinc-600 hover:border-red-500'
                  }`}>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoFileChange}
                      className="hidden"
                      id="video-upload"
                      disabled={newVideoShow.videoURL && newVideoShow.videoURL.trim() !== ''}
                    />
                    <label htmlFor="video-upload" className={`cursor-pointer ${
                      newVideoShow.videoURL ? 'cursor-not-allowed opacity-50' : ''
                    }`}>
                      <FaVideo className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-sm text-gray-400 mb-2">
                        {(selectedVideoFile || newVideoShow.videoFile) ? 'Archivo seleccionado' : 'Haz clic para seleccionar un video'}
                      </p>
                      <p className="text-xs text-gray-500">
                        MP4, AVI, MOV, WMV (máx. 1 GB)
                      </p>
                      {newVideoShow.videoURL && (
                        <p className="text-xs text-yellow-400 mt-2">
                          Deshabilitado porque hay una URL de video
                        </p>
                      )}
                    </label>
                  </div>
                  
                  {(selectedVideoFile || newVideoShow.videoFile) && (
                    <div className="bg-green-900/20 border border-green-500 rounded-lg p-3">
                      <p className="text-sm text-green-300">
                        <strong>Archivo seleccionado:</strong> {(selectedVideoFile || newVideoShow.videoFile)?.name}
                      </p>
                      {(selectedVideoFile || newVideoShow.videoFile) && (
                        <p className="text-xs text-green-400">
                          Tamaño: {((selectedVideoFile || newVideoShow.videoFile).size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* URL de Video */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-white">
                      O Proporcionar URL del Video
                    </label>
                    {/* Botón para limpiar URL */}
                    {newVideoShow.videoURL && newVideoShow.videoURL.trim() !== '' && (
                      <button
                        type="button"
                        onClick={clearVideoURL}
                        className="text-red-400 hover:text-red-300 p-1 rounded transition-colors"
                        title="Limpiar URL"
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  
                  <input
                    type="url"
                    name="videoURL"
                    value={newVideoShow.videoURL}
                    onChange={handleChange}
                    disabled={selectedVideoFile !== null || newVideoShow.videoFile !== null}
                    className={`w-full px-4 py-3 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                      (selectedVideoFile || newVideoShow.videoFile) ? 'bg-gray-700 border-gray-600 cursor-not-allowed' : 'bg-zinc-700/50 border-zinc-600'
                    }`}
                    placeholder="https://ejemplo.com/video.mp4"
                  />
                  
                  {(selectedVideoFile || newVideoShow.videoFile) && (
                    <p className="text-xs text-yellow-400">
                      Deshabilitado porque hay un archivo seleccionado
                    </p>
                  )}
                  
                  {newVideoShow.videoURL && !(selectedVideoFile || newVideoShow.videoFile) && (
                      <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-3">
                        <p className="text-sm text-blue-300 mb-1">
                          <strong>URL configurada:</strong>
                        </p>
                        <p className="text-xs text-blue-200 break-all whitespace-normal leading-relaxed">
                          {newVideoShow.videoURL}
                        </p>
                      </div>
                  )}
                </div>
              </div>
            </div>

            {/* SECCIÓN DE MINIATURA*/}
            <div className="border-t border-zinc-700 pt-8">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
                <FaImage className="mr-2 text-red-500" />
                Miniatura (Opcional)
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Subir Archivo de Miniatura */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-white">
                      Subir Archivo de Imagen
                    </label>
                    {/* Botón para limpiar archivo de miniatura */}
                    {(selectedThumbnailFile || newVideoShow.thumbnailFile) && (
                      <button
                        type="button"
                        onClick={clearThumbnailFile}
                        className="text-red-400 hover:text-red-300 p-1 rounded transition-colors"
                        title="Eliminar imagen seleccionada"
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
                    (selectedThumbnailFile || newVideoShow.thumbnailFile) ? 'border-green-500 bg-green-500/10' : 
                    newVideoShow.imagen ? 'border-gray-600 bg-gray-500/10' : 
                    'border-zinc-600 hover:border-red-500'
                  }`}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailFileChange}
                      className="hidden"
                      id="thumbnail-upload"
                      disabled={newVideoShow.imagen && newVideoShow.imagen.trim() !== ''}
                    />
                    <label htmlFor="thumbnail-upload" className={`cursor-pointer ${
                      newVideoShow.imagen ? 'cursor-not-allowed opacity-50' : ''
                    }`}>
                      <FaImage className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-sm text-gray-400 mb-2">
                        {(selectedThumbnailFile || newVideoShow.thumbnailFile) ? 'Imagen seleccionada' : 'Haz clic para seleccionar una imagen'}
                      </p>
                      <p className="text-xs text-gray-500">
                        JPG, PNG, GIF, WEBP (máx. 500 MB)
                      </p>
                    </label>
                  </div>
                  
                  {(selectedThumbnailFile || newVideoShow.thumbnailFile) && (
                    <div className="bg-green-900/20 border border-green-500 rounded-lg p-3">
                      <p className="text-sm text-green-300">
                        <strong>Miniatura seleccionada:</strong> {(selectedThumbnailFile || newVideoShow.thumbnailFile)?.name}
                      </p>
                    </div>
                  )}
                </div>

                {/* URL de Imagen */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-white">
                      O Proporcionar URL de Imagen
                    </label>
                    {/* Botón para limpiar URL de imagen */}
                    {newVideoShow.imagen && newVideoShow.imagen.trim() !== '' && (
                      <button
                        type="button"
                        onClick={clearImageURL}
                        className="text-red-400 hover:text-red-300 p-1 rounded transition-colors"
                        title="Limpiar URL de imagen"
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  
                  <input
                    type="url"
                    name="imagen"
                    value={newVideoShow.imagen}
                    onChange={handleChange}
                    disabled={(selectedThumbnailFile || newVideoShow.thumbnailFile) !== null}
                    className={`w-full px-4 py-3 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
                      (selectedThumbnailFile || newVideoShow.thumbnailFile) ? 'bg-gray-700 border-gray-600 cursor-not-allowed' : 'bg-zinc-700/50 border-zinc-600'
                    }`}
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                  
                  {newVideoShow.imagen && !(selectedThumbnailFile || newVideoShow.thumbnailFile) && (
                    <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-3">
                      <p className="text-sm text-blue-300 mb-1">
                        <strong>URL de imagen:</strong>
                      </p>
                      <p className="text-xs text-blue-200 break-all whitespace-normal leading-relaxed">
                        {newVideoShow.imagen}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* COMEDIANTES Y TAGS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-zinc-700 pt-8">
              {/* COMEDIANTES */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white flex items-center">
                    <FaUser className="mr-2 text-red-500" />
                    Comediantes
                  </label>
                  <span className="bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full">
                    {newVideoShow.comediantesIds.length} seleccionados
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar comediante..."
                      value={searchComediante}
                      onChange={(e) => {
                        setSearchComediante(e.target.value);
                        setComediantesPage(1);
                      }}
className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                    />
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => setShowSelectedComediantesOnly(!showSelectedComediantesOnly)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1
                      ${showSelectedComediantesOnly 
                        ? 'bg-red-600 text-white' 
                        : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'}`}
                  >
                    <FaCheck className={`h-3 w-3 ${showSelectedComediantesOnly ? 'opacity-100' : 'opacity-50'}`} />
                    <span>Seleccionados</span>
                  </button>
                </div>
                
                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
                  <div className="flex justify-between items-center px-3 py-2 bg-zinc-700/30 border-b border-zinc-700 text-xs text-gray-300">
                    <span>
                      {filteredComediantes.length} resultado{filteredComediantes.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        disabled={comediantesPage === 1}
                        onClick={() => setComediantesPage(prev => Math.max(prev - 1, 1))}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-600 disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        &lt;
                      </button>
                      <span>
                        {comediantesPage} / {totalPages || 1}
                      </span>
                      <button
                        type="button"
                        disabled={comediantesPage >= totalPages}
                        onClick={() => setComediantesPage(prev => Math.min(prev + 1, totalPages))}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-600 disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto pr-2 space-y-1 custom-scrollbar p-2">
                    {paginatedComediantes.length === 0 ? (
                      <p className="text-gray-400 text-sm p-2 text-center">
                        No se encontraron resultados
                      </p>
                    ) : (
                      paginatedComediantes.map((comediante) => {
                        const isSelected = newVideoShow.comediantesIds.includes(comediante.comedianteId);
                        return (
                          <div
                            key={comediante.comedianteId}
                            onClick={() => handleComedianteChange(comediante.comedianteId)}
                            className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-all duration-200 ${
                              isSelected ? 'bg-red-900/30 border border-red-600/50' : 'hover:bg-zinc-700/50 border border-zinc-700'
                            }`}
                          >
                            <span className="text-sm text-gray-200">{comediante.nombre}</span>
                            <span className={`flex items-center justify-center w-5 h-5 rounded-full 
                              ${isSelected ? 'bg-red-600' : 'bg-zinc-700 border border-zinc-600'}`}>
                              {isSelected && <FaCheck className="h-3 w-3 text-white" />}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {newVideoShow.comediantesIds.length > 0 && (
                    <div className="px-3 py-2 bg-zinc-700/30 border-t border-zinc-700">
                      <p className="text-xs text-gray-400 mb-1">Seleccionados:</p>
                      <div className="flex flex-wrap gap-1">
                        {newVideoShow.comediantesIds.map(id => {
                          const comediante = comediantes.find(c => c.comedianteId === id);
                          if (!comediante) return null;
                          
                          return (
                            <span 
                              key={id}
                              className="inline-flex items-center bg-red-900/20 text-red-300 text-xs px-2 py-1 rounded-full"
                            >
                              {comediante.nombre}
                              <FaTimes 
                                className="ml-1 cursor-pointer hover:text-white" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleComedianteChange(id);
                                }}
                              />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* TAGS */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white flex items-center">
                    <FaTags className="mr-2 text-red-500" />
                    Tags
                  </label>
                  <span className="bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full">
                    {newVideoShow.tagsIds.length} seleccionados
                  </span>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar tag..."
                    value={searchTag}
                    onChange={(e) => setSearchTag(e.target.value)}
                    className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                  />
                </div>
                
                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
                  <div className="max-h-48 overflow-y-auto p-3 custom-scrollbar">
                    {filteredTags.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center">No se encontraron resultados</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {filteredTags.map((tag) => {
                          const isSelected = newVideoShow.tagsIds.includes(tag.tagId);
                          const isDark = isColorDark(tag.colorHex);
                          
                          return (
                            <div
                              key={tag.tagId}
                              onClick={() => handleTagChange(tag.tagId)}
                              className={`inline-flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-200 cursor-pointer shadow-lg ${
                                isSelected ? 'ring-2' : 'hover:ring-1'
                              }`}
                              style={{ 
                                backgroundColor: `${tag.colorHex}20`,
                                borderColor: tag.colorHex,
                                color: isDark ? '#ffffff' : tag.colorHex,
                                border: `1px solid ${tag.colorHex}`,
                                ringColor: tag.colorHex,
                              }}
                            >
                              <span className="font-medium" style={{ 
                                textShadow: isDark ? '0 0 10px rgba(0,0,0,0.8)' : 'none',
                              }}>
                                {tag.nombre}
                              </span>
                              {isSelected && (
                                <span className="ml-2 flex items-center justify-center w-5 h-5 rounded-full" 
                                  style={{ 
                                    backgroundColor: isDark ? '#ffffff' : tag.colorHex,
                                    color: isDark ? tag.colorHex : '#ffffff',
                                  }}
                                >
                                  <FaCheck className="h-3 w-3" />
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {newVideoShow.tagsIds.length > 0 && (
                    <div className="px-3 py-2 bg-zinc-700/30 border-t border-zinc-700">
                      <p className="text-xs text-gray-400 mb-1">Seleccionados:</p>
                      <div className="flex flex-wrap gap-1">
                        {newVideoShow.tagsIds.map(id => {
                          const tag = tags.find(t => t.tagId === id);
                          if (!tag) return null;
                          
                          const isDark = isColorDark(tag.colorHex);
                          
                          return (
                            <span 
                              key={id}
                              className="inline-flex items-center text-xs px-2 py-1 rounded-full shadow-md"
                              style={{ 
                                backgroundColor: `${tag.colorHex}40`,
                                borderColor: tag.colorHex,
                                color: isDark ? '#ffffff' : tag.colorHex,
                                border: '1px solid',
                                textShadow: isDark ? '0 0 8px rgba(0,0,0,0.7)' : 'none',
                              }}
                            >
                              <span className="font-medium">{tag.nombre}</span>
                              <FaTimes 
                                className="ml-1 cursor-pointer hover:text-white" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTagChange(id);
                                }}
                              />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 pt-4 border-t border-zinc-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading || newVideoShow.comediantesIds.length === 0 || newVideoShow.tagsIds.length === 0}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center min-w-[140px] transition-all duration-200"
                title={
                  newVideoShow.comediantesIds.length === 0 ? 'Selecciona al menos un comediante' : 
                  newVideoShow.tagsIds.length === 0 ? 'Selecciona al menos un tag' : ''
                }
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creando...
                  </>
                ) : (
                  'Crear Video'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateVideoShowModal;